import { BetrokkenTrein, usePutBusEvacuatieEtas } from "@/entities/betrokken-treinen";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    ErrorDisplay,
    Form,
    FormFieldInput,
    SelectFormField,
    SelectOption,
} from "@/shared/components";
import { Time, timeToDate } from "@/shared/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

const etaToevoegenSchema = z.object({
    eta: z.iso.time({ error: (issue) => (issue.input === undefined ? "Vul de ETA in" : "Vul een geldige tijd in") }),
    aantalBussen: z.string({ message: "Vul het aantal bussen in" }).refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
        message: "Vul een geldig getal in",
    }),
});

type BusEvacuatieAanvraagDialogProps = {
    dossierId: string;
    trein: BetrokkenTrein;
    children: ReactNode;
};

type ETAToevoegenData = z.infer<typeof etaToevoegenSchema>;

export function BusEvacuatieEtaToevoegenDialog({ children, dossierId, trein }: BusEvacuatieAanvraagDialogProps) {
    const putEtas = usePutBusEvacuatieEtas();
    const [open, setOpen] = useState(false);

    const form = useForm<ETAToevoegenData>({
        resolver: zodResolver(etaToevoegenSchema),
        defaultValues: {
            aantalBussen: "1",
        },
    });

    function handleOpenChange(isOpen: boolean) {
        if (!isOpen) {
            form.reset();
        }
        setOpen(isOpen);
    }

    function onSubmit(data: ETAToevoegenData) {
        const mutateOptions = {
            onSuccess: () => handleOpenChange(false),
        };

        const etaDate = timeToDate(data.eta as Time);
        const currentEtas = trein.busEvacuatie?.etas ?? [];
        const newEtas = Array.from({ length: Number(data.aantalBussen) }).map(() => etaDate.toISOString());
        const totalEtas = currentEtas.map((eta) => eta.toISOString()).concat(newEtas);

        putEtas.mutate({ dossierId, treinId: trein.id, etas: totalEtas }, mutateOptions);
    }

    const besteld = trein.busEvacuatie!.aantalBussen;
    const geleverd = trein.busEvacuatie!.etas.length ?? 0;
    const aantalBussenOptions = getAantalBussenOptions(besteld - geleverd);

    if (besteld - geleverd === 0) {
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContainer>
                <DialogHeader>
                    <DialogTitle>ETA toevoegen evacuatiebus {BetrokkenTrein.formatTrainLabel(trein)}</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
                        <DialogBody className="grid gap-4">
                            <FormFieldInput form={form} name="eta" label="ETA" type="time" className="max-w-2/5" />
                            <p className="text-content-subtle text-s todohelptext leading-l">
                                Geef aan hoeveel bussen aankomen op dit tijdstip
                            </p>
                            <SelectFormField form={form} name="aantalBussen" options={aantalBussenOptions} label="Aantal bussen" />
                        </DialogBody>
                        <DialogFooter>
                            <Button type="button" variant="tertiary" onClick={() => handleOpenChange(false)}>
                                Annuleren
                            </Button>
                            <Button type="submit" variant="primary" disabled={putEtas.isPending}>
                                Toevoegen
                            </Button>
                            {putEtas.error && <ErrorDisplay error={putEtas.error} />}
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContainer>
        </Dialog>
    );
}

function getAantalBussenOptions(length: number): SelectOption[] {
    return Array.from({ length }, (_, i) => ({ label: (i + 1).toString(), value: (i + 1).toString() }));
}
